import { StepType } from "@redotech/redo-model/advanced-flow/common";
import { MESSAGE_CHANNELS_V3 } from "@redotech/redo-model/advanced-flow/rules-v3/types";
import { AssignV3Strategy } from "@redotech/redo-model/advanced-flow/rules/actions";
import { AssignmentTypeOption } from "@redotech/redo-model/advanced-flow/rules/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { SchemaType } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import {
  AI_NEEDS_HELP_TAG_NAME,
  AI_NO_REPLY_TAG_NAME,
} from "@redotech/redo-support-tags-common/system-tag-names";
import { Schema } from "effect";
import { makeBind } from "../field-binding";
import { DefaultRule } from "../types";

/**
 * Shape of this rule's `configurations` JSONB blob.
 * `channels` — the channels on which AI should be assigned.
 * `percentage` — random sampling rate (0–100). Mirrors
 * `team.settings.support.ai.aiAutopilotTicketVolume * 100`, defaulting to 100.
 */
export const AssignAiOnConversationCreatedConfigSchema = Schema.Struct({
  channels: Schema.Array(Schema.Literal(...MESSAGE_CHANNELS_V3)),
  percentage: Schema.Number,
});

export type AssignAiOnConversationCreatedConfig =
  typeof AssignAiOnConversationCreatedConfigSchema.Type;

const bind = makeBind<AssignAiOnConversationCreatedConfig>();

/**
 * Default rule: Assign AI on new ticket
 *
 * When a ticket is created on a channel that has AI enabled, the ticket is
 * unassigned, and it is NOT tagged AI_NEEDS_HELP, assign the AI agent. A random
 * percentage condition (matching the merchant's `aiAutopilotTicketVolume`
 * setting, defaulting to 100%) controls what fraction of tickets the AI takes.
 *
 * Steps:
 *   1. channel + unassigned + ¬AI_NEEDS_HELP  → true: step 2; false: stop
 *   2. random %                                → true: assign AI; false: stop
 */
export const assignAiOnConversationCreatedRule = {
  id: "default-assign-ai-on-conversation-created",
  name: "Assign AI on new ticket",
  description:
    "When an unassigned ticket is created on an AI-enabled channel and isn't tagged AI_NEEDS_HELP, assign the AI agent to handle it (subject to the configured ticket volume percentage).",
  hidden: false,
  triggerType: RuleTriggerType.CONVERSATION_CREATED,

  configSchema: AssignAiOnConversationCreatedConfigSchema,

  defaultConfig: {
    defaultRuleId: "default-assign-ai-on-conversation-created",
    enabled: false,
    sortIndex: 4,
    configurations: { channels: [], percentage: 100 },
  },

  template: {
    name: "Assign AI on new ticket",
    description:
      "When an unassigned ticket is created on an AI-enabled channel, assign the AI agent to handle it (subject to the configured ticket volume percentage).",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_CREATED,
        nextId: "channel-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "channel-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_CHANNEL,
              channels: bind("channels", []),
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.NO_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.HAS_TAG,
              tags: [AI_NEEDS_HELP_TAG_NAME],
              queryOperator: QueryOperator.IS_NOT,
            },
            {
              conditionType: RuleConditionType.HAS_TAG,
              tags: [AI_NO_REPLY_TAG_NAME],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "percentage-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "percentage-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.RANDOM_PERCENTAGE,
              percentage: bind("percentage", 100),
            },
          ],
        },
        nextTrueId: "assign-ai",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "assign-ai",
        actions: [
          {
            actionType: RuleActionType.ASSIGN_V3,
            mode: { _tag: "ai" },
            strategy: AssignV3Strategy.ROUND_ROBIN,
            maxTicketsPerUser: null,
          },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule<string, AssignAiOnConversationCreatedConfig>;
